/**
 * Rotates the social proof bar through its pool of people: the two names
 * crossfade to the next pair, then the three avatars follow. Respects
 * prefers-reduced-motion (swaps without animation) and pauses when the tab
 * is hidden.
 */
if (!customElements.get('lm-social-proof')) {
  customElements.define(
    'lm-social-proof',
    class LmSocialProof extends HTMLElement {
      connectedCallback() {
        if (this.dataset.rotate !== 'true') return;

        const data = this.querySelector('[data-lm-social-proof-people]');
        try {
          this.people = JSON.parse(data ? data.textContent : '[]');
        } catch (error) {
          this.people = [];
        }
        if (this.people.length < 3) return;

        this.namesEl = this.querySelector('[data-lm-social-proof-names]');
        this.avatarEls = Array.from(this.querySelectorAll('[data-lm-social-proof-avatar]'));
        this.badgeTemplate = this.querySelector('[data-lm-social-proof-badge]');
        this.reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        this.index = 0;
        this.interval = Math.max(2, Number(this.dataset.interval) || 5) * 1000;

        this.onVisibility = this.onVisibility.bind(this);
        document.addEventListener('visibilitychange', this.onVisibility);
        this.start();
      }

      disconnectedCallback() {
        this.stop();
        document.removeEventListener('visibilitychange', this.onVisibility);
      }

      onVisibility() {
        if (document.hidden) {
          this.stop();
        } else {
          this.start();
        }
      }

      start() {
        if (this.timer) return;
        this.timer = window.setInterval(() => this.advance(), this.interval);
      }

      stop() {
        window.clearInterval(this.timer);
        this.timer = null;
      }

      personAt(offset) {
        return this.people[(this.index + offset) % this.people.length];
      }

      advance() {
        this.index = (this.index + 1) % this.people.length;
        const first = this.personAt(0);
        const second = this.personAt(1);
        const third = this.personAt(2);

        this.swap(this.namesEl, () => this.renderNames(first, second));

        const avatarDelay = this.reduceMotion ? 0 : 350;
        window.setTimeout(() => {
          [first, second, third].forEach((person, i) => {
            const el = this.avatarEls[i];
            if (!el) return;
            this.swap(el, () => this.renderAvatar(el, person));
          });
        }, avatarDelay);
      }

      swap(el, apply) {
        if (!el) return;
        if (this.reduceMotion) {
          apply();
          return;
        }
        el.classList.add('is-swapping');
        window.setTimeout(() => {
          apply();
          el.classList.remove('is-swapping');
        }, 280);
      }

      renderNames(first, second) {
        const el = this.namesEl;
        el.textContent = '';
        [first, second].forEach((person, i) => {
          if (!person) return;
          if (i > 0) el.appendChild(document.createTextNode(', '));
          const strong = document.createElement('strong');
          strong.textContent = person.name;
          el.appendChild(strong);
          if (this.badgeTemplate) {
            el.appendChild(this.badgeTemplate.content.cloneNode(true));
          }
        });
      }

      renderAvatar(el, person) {
        el.textContent = '';
        if (person.avatar) {
          const img = document.createElement('img');
          img.src = person.avatar;
          img.width = 96;
          img.height = 96;
          img.alt = '';
          img.loading = 'lazy';
          el.appendChild(img);
        } else {
          el.textContent = (person.name || '+').charAt(0).toUpperCase();
        }
      }
    }
  );
}
